import { type Ref, type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, Settings, Mic, X } from "lucide-react";
import { VoiceScene } from "./VoiceScene";
import { BrainStrip } from "./BrainStrip";
import type { VoiceState } from "../voice/types";
import { useSpeechToText } from "../hooks/useSpeechToText";
import { useVoiceRecorder } from "../hooks/useVoiceRecorder";
import { audioDebug } from "../voice/debug-overlay";

interface VoiceZoneProps {
  voiceState: VoiceState;
  getAudioLevel: () => number;
  companionName: string;
  connectionStatus: "connected" | "connecting" | "reconnecting" | "switching";
  transcriptOpen: boolean;
  onToggleTranscript: () => void;
  onSettingsOpen: () => void;
  onExitVoiceMode: () => void;
  connected: boolean;
  modelLabel: string;
  constellationTouch: "off" | "gentle" | "full";
  /** Ref placed on the first interactive element — receives focus when entering voice mode */
  firstFocusRef?: Ref<HTMLButtonElement>;
  sttAvailable?: boolean;
  sendSTTAudio?: (blob: Blob, format?: string) => void;
  onSTTResultRef?: MutableRefObject<((text: string) => void) | null>;
  onSend?: (text: string) => void;
  sttStreaming?: import("../hooks/useStreamingSTT").SttStreamingProps;
}

const STATUS_LABELS: Record<string, string> = {
  connected: "Connected",
  connecting: "Connecting...",
  reconnecting: "Reconnecting...",
  switching: "Switching...",
};

export function VoiceZone({
  voiceState,
  getAudioLevel,
  companionName,
  connectionStatus,
  transcriptOpen,
  onToggleTranscript,
  onSettingsOpen,
  onExitVoiceMode,
  connected,
  modelLabel,
  constellationTouch,
  firstFocusRef,
  sttAvailable,
  sendSTTAudio,
  onSTTResultRef,
  onSend,
  sttStreaming,
}: VoiceZoneProps) {
  // STT: transcript → auto-send as message (no text input in voice mode)
  // Time guard: iOS Safari fires rapid "final" events (~150ms apart) for one utterance
  const lastSendTimeRef = useRef(0);
  const handleTranscript = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !onSend) return;
    const now = Date.now();
    if (now - lastSendTimeRef.current < 500) return;
    lastSendTimeRef.current = now;
    onSend(trimmed);
  }, [onSend]);

  // Web Speech API (primary) — finalOnly prevents interim fragments from firing as messages
  const {
    isListening: webSttListening,
    toggle: webSttToggle,
    start: webSttStart,
    stop: webSttStop,
    supported: sttSupported,
  } = useSpeechToText(handleTranscript, { finalOnly: true });

  // Server-side STT (fallback when Web Speech API unavailable)
  const handleRecordingResult = useCallback((blob: Blob) => {
    sendSTTAudio?.(blob, 'webm');
  }, [sendSTTAudio]);
  const recorder = useVoiceRecorder(handleRecordingResult);

  // Wire server STT result → auto-send as message
  useEffect(() => {
    if (onSTTResultRef) {
      onSTTResultRef.current = (text: string) => {
        if (text) handleTranscript(text);
      };
    }
    return () => {
      if (onSTTResultRef) onSTTResultRef.current = null;
    };
  }, [handleTranscript, onSTTResultRef]);

  // On Android, prefer server-side STT — SpeechRecognition beeps on start()
  // and stop() doesn't reliably fire onend, causing a stuck yellow mic.
  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const useServerSTT = Boolean(sttAvailable) && recorder.supported && (isAndroid || !sttSupported);
  // On Android, disable web-speech entirely when server STT isn't available
  const isListening = useServerSTT ? recorder.isRecording
    : (!isAndroid && sttSupported) ? webSttListening
    : false;
  const canSTT = useServerSTT ? recorder.supported
    : (!isAndroid && sttSupported) ? sttSupported
    : false;

  // Echo prevention: pause STT while TTS is playing, resume when done.
  // Refs prevent the effect from re-firing when isListening / recorder change.
  const wasListeningRef = useRef(false);
  const isListeningRef = useRef(false);
  isListeningRef.current = isListening;

  const recorderStopRef = useRef(recorder.stop);
  const recorderStartRef = useRef(recorder.start);
  recorderStopRef.current = recorder.stop;
  recorderStartRef.current = recorder.start;

  const webSttStopRef = useRef(webSttStop);
  const webSttStartRef = useRef(webSttStart);
  webSttStopRef.current = webSttStop;
  webSttStartRef.current = webSttStart;

  const [micAnnouncement, setMicAnnouncement] = useState("");
  const micAnnouncementTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (voiceState === "speaking" && isListeningRef.current) {
      audioDebug('stt', `echo prevention: stopping STT (voiceState=${voiceState})`);
      wasListeningRef.current = true;
      if (useServerSTT) recorderStopRef.current();
      else if (!isAndroid && sttSupported) webSttStopRef.current();
      setMicAnnouncement("Microphone paused");
      if (micAnnouncementTimerRef.current) clearTimeout(micAnnouncementTimerRef.current);
      micAnnouncementTimerRef.current = setTimeout(() => setMicAnnouncement(""), 3000);
    } else if (voiceState !== "speaking" && wasListeningRef.current) {
      audioDebug('stt', `echo prevention: restarting STT (voiceState=${voiceState})`);
      wasListeningRef.current = false;
      if (useServerSTT) recorderStartRef.current();
      else if (!isAndroid && sttSupported) webSttStartRef.current();
      setMicAnnouncement("Microphone resumed");
      if (micAnnouncementTimerRef.current) clearTimeout(micAnnouncementTimerRef.current);
      micAnnouncementTimerRef.current = setTimeout(() => setMicAnnouncement(""), 3000);
    }
    return () => {
      if (micAnnouncementTimerRef.current) clearTimeout(micAnnouncementTimerRef.current);
    };
  }, [voiceState, useServerSTT, isAndroid, sttSupported]);

  function handleMicToggle() {
    if (useServerSTT) {
      recorder.toggle();
    } else if (!isAndroid && sttSupported) {
      webSttToggle();
    }
    // Android + no server STT = no STT available → button is disabled via canSTT
  }

  return (
    <div
      className={`voice-zone${transcriptOpen ? " transcript-open" : ""}`}
      role="main"
      aria-label="Voice mode"
    >
      {/* Single Three.js canvas — constellation + orb in one renderer */}
      <VoiceScene
        sensitivity={constellationTouch}
        voiceState={voiceState}
        getAudioLevel={getAudioLevel}
        visible
      />

      {/* CSS glow halo — radial gradient overlay that creates the "dark planet" depth effect */}
      <div className={`orb-glow state-${voiceState}`} aria-hidden="true" />

      {/* Utility controls — top right */}
      <div className="voice-zone__controls">
        <button
          ref={firstFocusRef}
          className={`btn btn-icon btn-ghost${transcriptOpen ? " active" : ""}`}
          onClick={onToggleTranscript}
          aria-label={transcriptOpen ? "Close transcript" : "Open transcript"}
          aria-pressed={transcriptOpen}
        >
          <MessageSquare size={18} aria-hidden="true" />
        </button>
        <button
          className="btn btn-icon btn-ghost"
          onClick={onSettingsOpen}
          aria-label="Settings"
        >
          <Settings size={18} aria-hidden="true" />
        </button>
        <button
          className="voice-zone__exit-btn"
          onClick={onExitVoiceMode}
          aria-label="Back to chat"
        >
          <X size={16} aria-hidden="true" />
          <span>Chat</span>
        </button>
      </div>

      {/* Companion identity + mic — anchored to bottom of voice zone */}
      <div className="voice-ui-layer">
        <div className="voice-companion">
          <div className="voice-companion__name display">{companionName.toUpperCase()}</div>
          <div className="voice-companion__status">
            <span
              className={`status-dot ${connectionStatus === "connected" ? "connected" : "disconnected"}`}
              aria-hidden="true"
            />
            {STATUS_LABELS[connectionStatus] ?? "Connecting..."}
          </div>
          {/* Announces urgent connection status changes */}
          <span
            className="sr-only"
            aria-live="polite"
            aria-atomic="true"
          >
            {companionName}, {STATUS_LABELS[connectionStatus] ?? "Connecting"}
            {(() => {
              const voiceStateLabel =
                voiceState === "speaking"
                  ? "Speaking"
                  : voiceState === "ready"
                  ? "Ready"
                  : voiceState === "loading"
                  ? "Loading"
                  : voiceState === "error"
                  ? "Error"
                  : "";
              return voiceStateLabel ? `, ${voiceStateLabel}` : "";
            })()}
          </span>
        </div>

        {/* Mic button — push-to-talk STT */}
        <button
          className={`btn-mic state-${voiceState}${isListening ? " listening" : ""}`}
          onClick={handleMicToggle}
          disabled={!canSTT}
          aria-label={isListening ? "Stop listening" : "Start listening"}
        >
          <Mic size={24} aria-hidden="true" />
        </button>
      </div>

      {/* Brain strip at the bottom */}
      <div className="voice-zone__brain">
        {/* companionName was previously omitted here, so BrainStrip fell back
            to persona.ts's build-time DEFAULT_PERSONA_NAME instead of the
            active session's real companion (which VoiceZone already receives
            as a prop and displays above in voice-companion__name). */}
        <BrainStrip connected={connected} modelLabel={modelLabel} companionName={companionName} />
      </div>

      {/* Announces mic auto-pause/resume triggered by echo prevention.
          Kept as direct child of voice-zone (not inside voice-ui-layer) so
          Firefox+NVDA doesn't drop it when a sibling live region is also active. */}
      <span
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {micAnnouncement}
      </span>
    </div>
  );
}
